import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { mdiLinkVariant, mdiAlertCircleOutline } from '@mdi/js'
import { useJobs, type JobExecutionResponse } from '../../api/jobs'

interface ResolutionStep {
    asset_type_code: string
    matched_uid?: string
    confidence: number
    method: string
    reasoning?: string
    candidate_count?: number
    margin?: number
}

interface ResolutionOutput {
    document_uid?: string
    steps: ResolutionStep[]
    min_confidence: number
    outcome: 'auto_applied' | 'suggested' | 'low_confidence' | 'proposal_created' | 'no_match' | string
    matched_asset_uid?: string
}

function parseOutput(exec: JobExecutionResponse | undefined): ResolutionOutput | null {
    if (!exec?.output) return null
    // JobExecution.Output is stored as JSONB; the hook gives us a Record. Not
    // every run writes a trace: a document that kept its deposit's asset
    // (outcome `kept_deposit_asset` / `descended_from_deposit_asset`) has no
    // `steps`, and reading `.length` on it crashed the page (#1548).
    const output = exec.output as unknown as Partial<ResolutionOutput>
    if (!Array.isArray(output.steps)) return null
    return output as ResolutionOutput
}

// NIL_UUID is Go's zero uuid.UUID value. hierarchyStepResult.MatchedUID is a
// [16]byte array, so Go's `omitempty` never actually omits it (omitempty
// only recognizes empty strings/0/nil/empty slices-maps/false, never a zero
// array) — every unmatched step still serializes matched_uid as this literal
// string rather than omitting the field. hasMatch must check for it
// explicitly; a plain truthiness check on the string is always true.
const NIL_UUID = '00000000-0000-0000-0000-000000000000'

function hasMatch(matchedUid: string | undefined): matchedUid is string {
    return !!matchedUid && matchedUid !== NIL_UUID
}

// Method → i18n key + Tailwind color classes. Kept as a lookup rather than a
// switch so an unrecognized future method value still renders (falls back to
// the raw string + neutral styling) instead of disappearing.
const METHOD_STYLES: Record<string, string> = {
    filename_code: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    identifier_lookup: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    address_component_match: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    cached_resolution: 'bg-teal-500/15 text-teal-700 dark:text-teal-300',
    geocode_proximity: 'bg-indigo-500/15 text-indigo-700 dark:text-indigo-300',
    signal_scoring: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    gemini: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
    small_portfolio: 'bg-muted text-muted-foreground',
    single_candidate: 'bg-muted text-muted-foreground',
    proposal: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    no_match: 'bg-muted text-muted-foreground',
}

const OUTCOME_STYLES: Record<string, string> = {
    auto_applied: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
    suggested: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
    proposal_created: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
    low_confidence: 'bg-muted text-muted-foreground',
    no_match: 'bg-muted text-muted-foreground',
}

export interface AssetResolutionTraceProps {
    org: string
    documentUid: string
}

/**
 * Renders the resolution trace of the latest doc_asset_association run for a
 * document: which rung matched (filename code / stage A identifier / stage
 * B-C scoring / Gemini / a to_approve asset proposal), at what confidence,
 * and why — from JobExecution.Output.steps[] (spec
 * 2026-07-09-01-attach-docs-to-assets-before-deep-extraction.md §5).
 *
 * Silently renders nothing when no doc_asset_association job exists for the
 * document (e.g. it already had an asset at upload time, so association
 * never ran).
 */
export function AssetResolutionTrace({ org, documentUid }: AssetResolutionTraceProps) {
    const { t } = useTranslation('documents')

    const { data, isLoading } = useJobs(org, {
        type: 'doc_asset_association',
        target_entity_type: 'document',
        target_entity_uid: documentUid,
        limit: 1,
    })

    const job = data?.jobs?.[0]
    const execution = job?.last_execution
    const output = useMemo(() => parseOutput(execution), [execution])

    if (isLoading) return null
    if (!job) return null

    if (execution && (execution.status === 'pending' || execution.status === 'running')) {
        return (
            <div className="mt-6 bg-card border border-border rounded-lg p-4" data-testid="resolution-trace-pending">
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <AppIcon path={mdiLinkVariant} size="md" />
                    <span>{t('resolution_trace.status_pending')}</span>
                </div>
            </div>
        )
    }

    if (execution && execution.status === 'failed') {
        return (
            <div className="mt-6 bg-card border border-destructive/40 rounded-lg p-4" data-testid="resolution-trace-failed">
                <div className="flex items-center gap-2 text-destructive text-sm">
                    <AppIcon path={mdiAlertCircleOutline} size="md" />
                    <span>{t('resolution_trace.status_failed')}</span>
                </div>
            </div>
        )
    }

    if (!output || output.steps.length === 0) {
        return null
    }

    return (
        <div className="mt-6 bg-card border border-border rounded-lg" data-testid="resolution-trace-summary">
            <div className="p-4 border-b border-border flex items-center gap-2">
                <AppIcon path={mdiLinkVariant} size="md" className="text-primary" />
                <h3 className="font-medium">{t('resolution_trace.title')}</h3>
                <span
                    className={`ml-auto text-xs px-2 py-0.5 rounded-full ${OUTCOME_STYLES[output.outcome] ?? 'bg-muted text-muted-foreground'}`}
                    data-testid="resolution-trace-outcome"
                >
                    {t(`resolution_trace.outcome.${output.outcome}`, { defaultValue: output.outcome })}
                </span>
            </div>

            {output.outcome === 'proposal_created' && (
                <div className="p-4 border-b border-border text-sm text-muted-foreground" data-testid="resolution-trace-proposal-hint">
                    <AppIcon path={mdiAlertCircleOutline} size="sm" className="inline mr-1" />
                    {t('resolution_trace.proposal_hint')}
                </div>
            )}

            <ul className="divide-y divide-border">
                {output.steps.map((step, idx) => (
                    <li key={`${step.asset_type_code}-${idx}`} className="p-4 text-sm" data-testid={`resolution-trace-step-${idx}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                                {step.asset_type_code}
                            </span>
                            <span
                                className={`text-xs px-1.5 py-0.5 rounded ${METHOD_STYLES[step.method] ?? 'bg-muted text-muted-foreground'}`}
                                data-testid={`resolution-trace-step-${idx}-method`}
                            >
                                {t(`resolution_trace.method.${step.method}`, { defaultValue: step.method })}
                            </span>
                            {hasMatch(step.matched_uid) && (
                                <span className="text-muted-foreground">
                                    {t('resolution_trace.confidence', { confidence: step.confidence })}
                                </span>
                            )}
                            {hasMatch(step.matched_uid) && (
                                <Link
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: step.matched_uid }}
                                    className="text-foreground hover:underline ml-auto"
                                    data-testid={`resolution-trace-step-${idx}-asset-link`}
                                >
                                    {t('resolution_trace.view_asset')}
                                </Link>
                            )}
                        </div>
                        {step.reasoning && (
                            <p className="mt-1 text-muted-foreground">{step.reasoning}</p>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    )
}
